import { Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';
import { Navigation } from '../components/layout/Navigation';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { AccountCard } from '../components/accounts/AccountCard';
import { AccountForm } from '../components/accounts/AccountForm';
import { useAccounts, useCreateAccount, useUpdateAccount, useDeleteAccount } from '../hooks/useAccounts';
import { useTransactions } from '../hooks/useTransactions';
import { CreateAccountRequest, Account, UpdateAccountRequest, ACCOUNT_TYPE_LABELS } from '../types/account';
import { formatCurrency } from '../lib/currency';
import { useTranslation } from 'react-i18next';

export function AccountsPage() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: accounts, isLoading } = useAccounts();
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();
  const toast = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<Account | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Fetch transactions for the account being deleted
  const { data: accountTransactions } = useTransactions(
    deletingAccount ? { account_id: deletingAccount.id } : undefined
  );

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">{t('common:loading')}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const handleCreateAccount = async (data: CreateAccountRequest) => {
    try {
      await createAccount.mutateAsync(data);
      toast.success(t('accounts:messages.created'));
      setIsModalOpen(false);
    } catch (error) {
      toast.error(t('accounts:messages.failed_create'));
    }
  };

  const handleEditAccount = (account: Account) => {
    setEditingAccount(account);
  };

  const handleUpdateAccount = async (data: CreateAccountRequest) => {
    if (!editingAccount) return;

    try {
      const updateData: UpdateAccountRequest = {
        name: data.name,
        description: data.description,
        color: data.color,
      };
      await updateAccount.mutateAsync({ id: editingAccount.id, data: updateData });
      toast.success(t('accounts:messages.updated'));
      setEditingAccount(null);
    } catch (error) {
      toast.error(t('accounts:messages.failed_update'));
    }
  };

  const handleCloseEditModal = () => {
    setEditingAccount(null);
  };

  const handleDeleteAccount = (account: Account) => {
    setDeletingAccount(account);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAccount) return;

    const count = accountTransactions?.length || 0;

    // If account has transactions, don't allow deletion
    if (count > 0) {
      toast.error(t('accounts:messages.cannot_delete_with_transactions'));
      return;
    }

    // Proceed with deletion
    try {
      await deleteAccount.mutateAsync(deletingAccount.id);
      toast.success(t('accounts:messages.deleted'));
      setDeletingAccount(null);
    } catch (error) {
      toast.error(t('accounts:messages.failed_delete'));
    }
  };

  const handleArchiveAccount = async () => {
    if (!deletingAccount) return;

    try {
      const updateData: UpdateAccountRequest = {
        is_active: false,
      };
      await updateAccount.mutateAsync({ id: deletingAccount.id, data: updateData });
      toast.success(t('accounts:messages.archived'));
      setDeletingAccount(null);
    } catch (error) {
      toast.error(t('accounts:messages.failed_archive'));
    }
  };

  const handleRestoreAccount = async (account: Account) => {
    try {
      const updateData: UpdateAccountRequest = {
        is_active: true,
      };
      await updateAccount.mutateAsync({ id: account.id, data: updateData });
      toast.success(t('accounts:messages.restored'));
    } catch (error) {
      toast.error(t('accounts:messages.failed_restore'));
    }
  };

  // Apply filters
  const filteredAccounts = accounts?.filter(acc => {
    // Filter by type
    if (typeFilter && acc.type !== typeFilter) {
      return false;
    }

    // Filter by status
    if (statusFilter === 'active' && !acc.is_active) {
      return false;
    }
    if (statusFilter === 'archived' && acc.is_active) {
      return false;
    }

    return true;
  }) || [];

  // Separate active and archived accounts
  const activeAccounts = filteredAccounts.filter(acc => acc.is_active);
  const archivedAccounts = filteredAccounts.filter(acc => !acc.is_active);

  const totalBalance = activeAccounts.reduce((sum, acc) => {
    if (acc.type === 'credit_card') {
      return sum + acc.balance;
    }
    return sum + acc.balance;
  }, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('accounts:title')}</h1>
          <p className="text-gray-600 mt-1">
            {t('accounts:subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="text-sm text-gray-600 mb-1">{t('accounts:total_balance')}</div>
          <div className={`text-4xl font-bold ${totalBalance >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
            {formatCurrency(totalBalance)}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('accounts:all_types')}</option>
              <option value="bank">{t('accounts:types.bank')}</option>
              <option value="cash">{t('accounts:types.cash')}</option>
              <option value="credit_card">{t('accounts:types.credit_card')}</option>
              <option value="savings">{t('accounts:types.savings')}</option>
              <option value="investment">{t('accounts:types.investment')}</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('accounts:all_statuses')}</option>
              <option value="active">{t('accounts:status_active')}</option>
              <option value="archived">{t('accounts:status_archived')}</option>
            </select>

            <div className="ml-auto">
              <Button onClick={() => setIsModalOpen(true)} variant="primary">
                {t('accounts:add_account')}
              </Button>
            </div>
          </div>

          {(typeFilter || statusFilter) && (
            <button
              onClick={() => {
                setTypeFilter('');
                setStatusFilter('');
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {t('accounts:clear_filters')}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">{t('common:loading')}</p>
          </div>
        ) : (
          <>
            {/* Active Accounts */}
            {activeAccounts.length > 0 ? (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {activeAccounts.map((account) => (
                  <AccountCard
                    key={account.id}
                    account={account}
                    onEdit={handleEditAccount}
                    onDelete={handleDeleteAccount}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <p className="text-gray-600 mb-4">
                  {t('accounts:empty_state')}
                </p>
                <Button onClick={() => setIsModalOpen(true)} variant="primary">
                  {t('accounts:create_first_account')}
                </Button>
              </div>
            )}

            {/* Archived Accounts Section */}
            {archivedAccounts.length > 0 && (
              <div className="mt-8">
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
                >
                  <svg
                    className={`w-5 h-5 transition-transform ${showArchived ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="font-medium">
                    {t('accounts:archived_accounts')} ({archivedAccounts.length})
                  </span>
                </button>

                {showArchived && (
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {archivedAccounts.map((account) => (
                      <div key={account.id} className="relative">
                        <div className="absolute top-2 left-2 z-10 bg-gray-600 text-white text-xs px-2 py-1 rounded">
                          {t('accounts:archived')}
                        </div>
                        <div className="opacity-75">
                          <AccountCard
                            account={account}
                            onEdit={handleEditAccount}
                            onDelete={handleDeleteAccount}
                          />
                        </div>
                        <div className="px-4 pb-4 flex gap-2">
                          <Button
                            onClick={() => handleRestoreAccount(account)}
                            variant="primary"
                            className="flex-1 text-sm"
                            disabled={updateAccount.isPending}
                          >
                            {t('accounts:restore')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={t('accounts:add_account')}
        >
          <AccountForm
            onSubmit={handleCreateAccount}
            onCancel={() => setIsModalOpen(false)}
            isLoading={createAccount.isPending}
            mode="create"
          />
        </Modal>

        <Modal
          isOpen={!!editingAccount}
          onClose={handleCloseEditModal}
          title={t('accounts:edit_account')}
        >
          <AccountForm
            onSubmit={handleUpdateAccount}
            onCancel={handleCloseEditModal}
            isLoading={updateAccount.isPending}
            initialData={editingAccount || undefined}
            mode="edit"
          />
        </Modal>

        {/* Delete/Archive Confirmation */}
        {deletingAccount && (accountTransactions?.length || 0) === 0 ? (
          <ConfirmDialog
            isOpen={!!deletingAccount}
            onClose={() => setDeletingAccount(null)}
            onConfirm={handleConfirmDelete}
            title={t('accounts:delete_account')}
            message={
              <>
                {t('accounts:delete_confirm')}
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">{deletingAccount.name}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {t(`accounts:types.${deletingAccount.type}`)}
                  </p>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  {t('accounts:delete_warning')}
                </p>
              </>
            }
            confirmText={t('common:delete')}
            cancelText={t('common:cancel')}
            variant="danger"
            isLoading={deleteAccount.isPending}
          />
        ) : deletingAccount && (accountTransactions?.length || 0) > 0 ? (
          <ConfirmDialog
            isOpen={!!deletingAccount}
            onClose={() => setDeletingAccount(null)}
            onConfirm={handleArchiveAccount}
            title={t('accounts:cannot_delete_title')}
            message={
              <>
                {t('accounts:has_transactions', { count: accountTransactions?.length || 0 })}
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">{deletingAccount.name}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {t(`accounts:types.${deletingAccount.type}`)}
                  </p>
                </div>
                <p className="mt-3 text-sm text-gray-600">
                  {t('accounts:archive_instead')}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  {t('accounts:archive_explanation')}
                </p>
              </>
            }
            confirmText={t('accounts:archive_account')}
            cancelText={t('common:cancel')}
            variant="primary"
            isLoading={updateAccount.isPending}
          />
        ) : null}
      </div>
    </div>
  );
}
